import { useState } from 'react';

import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, formatDateTime, formatPhone, useAsync } from '@solar/ui';

import { api } from '../api';

/**
 * 시민회원 QNA — 레거시 board/citizen 파리티.
 *
 * 시민이 남긴 비밀 문의라 열람/답변은 콜센터(9)+에너지공사(1,2)만 가능하다
 * (사이드바에서 역할을 거르고, 서버도 403 으로 이중 방어). 삭제는 레거시에
 * 라우트 자체가 없었고 서버도 403 으로 거부하므로 버튼을 두지 않는다.
 * 답변 저장(res_content) 시 responded_at/답변자는 서버가 함께 갱신한다.
 */

/** 답변 여부 필터 — 레거시 searchResType('N' 대기 / 'Y' 응답). */
type RespondedFilter = 'all' | 'pending' | 'done';

const SIZE = 30;

export function CitizenQnaManage({ initialResponded }: { initialResponded?: boolean }) {
  const [filter, setFilter] = useState<RespondedFilter>(
    initialResponded === undefined ? 'all' : initialResponded ? 'done' : 'pending',
  );
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);

  const list = useAsync(
    () =>
      api.listBoards('citizen-qna', {
        responded: filter === 'all' ? undefined : filter === 'done',
        keyword: applied || undefined,
        page,
        size: SIZE,
      }),
    [filter, applied, page],
  );

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  const chips: Array<{ key: RespondedFilter; label: string }> = [
    { key: 'all', label: '전체' },
    { key: 'pending', label: '대기' },
    { key: 'done', label: '답변완료' },
  ];

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              className={filter === c.key ? 'chip active' : 'chip'}
              onClick={() => {
                setFilter(c.key);
                setPage(1);
              }}
            >
              {c.label}
            </button>
          ))}

          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword.trim());
              setPage(1);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="제목 또는 내용"
            />
            <button type="submit">검색</button>
          </form>
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>No</th>
                <th>문의 제목</th>
                <th>글쓴이</th>
                <th>등록일시</th>
                <th>응답여부</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    등록된 문의가 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((b) => (
                <tr
                  key={b.id}
                  className={b.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(b.id)}
                >
                  <td>{b.id}</td>
                  <td>
                    {/* 시민 포털에서 비밀글로 다뤄지는 문의라 잠금 표식을 붙인다. */}
                    <span role="img" aria-label="비밀글" title="비밀글">
                      🔒
                    </span>{' '}
                    {b.title}
                  </td>
                  <td>{b.user_name ?? '-'}</td>
                  <td>{formatDateTime(b.created_at)}</td>
                  <td>
                    {b.responded_at ? (
                      <span className="badge s4">답변완료</span>
                    ) : (
                      <span className="badge s2">대기</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>

      <aside className="admin__aside">
        {selected === null ? (
          <p className="muted">문의를 선택하세요.</p>
        ) : (
          <QnaDetail
            key={selected}
            qnaId={selected}
            onClose={() => setSelected(null)}
            onChanged={() => list.reload()}
          />
        )}
      </aside>
    </div>
  );
}

function QnaDetail({
  qnaId,
  onClose,
  onChanged,
}: {
  qnaId: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detail = useAsync(() => api.getBoard('citizen-qna', qnaId), [qnaId]);
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function saveAnswer(value: string) {
    if (!value.trim()) {
      setError('답변을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api.updateBoard('citizen-qna', qnaId, { res_content: value });
      setOk('답변을 저장했습니다.');
      setAnswer(null);
      detail.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '답변을 저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  if (detail.loading) return <Spinner />;
  if (detail.error) return <ErrorBox message={detail.error} onRetry={detail.reload} />;
  const post = detail.data;
  if (!post) return null;

  // 답변 폼 초기값 — 수정 시작 전에는 서버 값을 그대로 보여준다.
  const draft = answer ?? post.res_content ?? '';

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>{post.title}</h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <h3 className="section">문의 내용</h3>
      <section className="panel">
        <div className="field">
          <span className="field__label">문의자 이름</span>
          <span className="field__value">{post.user_name ?? '-'}</span>
        </div>
        <div className="field">
          <span className="field__label">문의자 번호</span>
          <span className="field__value">{formatPhone(post.user_phone)}</span>
        </div>
        <div className="field">
          <span className="field__label">등록일시</span>
          <span className="field__value">{formatDateTime(post.created_at)}</span>
        </div>
        <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{post.contents ?? '-'}</p>
      </section>

      <h3 className="section">답변 달기</h3>
      <section className="panel">
        {post.responded_at && (
          <div className="field">
            <span className="field__label">답변일시</span>
            <span className="field__value">{formatDateTime(post.responded_at)}</span>
          </div>
        )}
        <textarea
          rows={6}
          value={draft}
          placeholder="답변을 입력해 주세요."
          onChange={(e) => setAnswer(e.target.value)}
        />
        {error && <p className="error">{error}</p>}
        {ok && <p className="ok">{ok}</p>}
        <div className="row form-actions">
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => void saveAnswer(draft)}
          >
            {busy ? '저장 중…' : post.responded_at ? '답변 수정' : '답변 등록'}
          </button>
        </div>
      </section>
    </div>
  );
}
